import React, { useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { AlertCircle, CheckCircle2, Info } from "lucide-react";
import ManagerLeagues from "./ManagerLeagues";

export type LandingPageProps = {
  className?: string;
};

function isValidFplId(value: string) {
  return /^\d{1,10}$/.test(value) && Number(value) > 0;
}

export default function LandingPage({ className }: LandingPageProps) {
  const [fplId, setFplId] = useState("");
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [submittedId, setSubmittedId] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const DEFAULT_FPL_ID = process.env.DEFAULT_FPL_ID
    ? Number(process.env.DEFAULT_FPL_ID)
    : 2356022;

  const valid = useMemo(() => isValidFplId(fplId), [fplId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!valid) {
      console.log("not valid");
      inputRef.current?.focus();
      return;
    }

    try {
      setBusy(true);
      const idNum = Number(fplId);
      setSubmittedId(idNum); // Pass ID to ManagerLeagues
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className={`min-h-dvh w-full bg-app relative overflow-hidden ${
        className ?? ""
      }`}
    >
      <div className="pointer-events-none absolute -top-24 -left-24 h-80 w-80 rounded-full blur-3xl bg-accent/10" />
      <div className="pointer-events-none absolute -bottom-24 -right-24 h-96 w-96 rounded-full blur-3xl bg-accent/10" />

      <div className="relative mx-auto grid min-h-dvh max-w-5xl md:place-items-center px-4 md:py-16">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="w-full"
        >
          {/* If we haven't submitted yet, show the form */}
          {!submittedId ? (
            <div className="mx-auto max-w-2xl bg-surface text-content backdrop-blur-xl shadow-2xl rounded-2xl border border-line">
              <div className="border-b border-line p-6">
                <h1 className="text-center text-3xl md:text-4xl font-semibold tracking-tight">
                  Fantasy Premier League
                  <span className="block text-base md:text-lg font-normal text-content-secondary mt-2">
                    Enter your FPL ID to get started
                  </span>
                  <span className="block text-base md:text-lg font-normal text-content-secondary mt-2">
                    Or see an{" "}
                    <button
                      type="button"
                      onClick={() => setSubmittedId(DEFAULT_FPL_ID)}
                      className="underline bg-transparent border-none cursor-pointer text-inherit inline-flex min-h-11 items-center px-1"
                    >
                      Example
                    </button>
                  </span>
                </h1>
              </div>
              <div className="p-3 md:p-8">
                <form onSubmit={handleSubmit} noValidate className="space-y-6">
                  <div>
                    <label
                      htmlFor="fplID"
                      className="mb-2 block text-sm font-medium text-content-secondary"
                    >
                      Your FPL ID
                    </label>
                    <div className="flex gap-3">
                      <input
                        ref={inputRef}
                        id="fplId"
                        inputMode="numeric"
                        pattern="\\d*"
                        placeholder="e.g. 1234567"
                        className="flex-1 min-h-11 rounded-md bg-surface-raised border border-line text-content placeholder:text-content-muted px-3 py-2 focus:outline-none focus:ring-2 focus:ring-accent"
                        value={fplId}
                        onChange={(e) => setFplId(e.target.value.trim())}
                        onBlur={() => setTouched(true)}
                        aria-invalid={touched && !valid}
                        aria-describedby="fplIdHelp"
                      />
                      <button
                        type="submit"
                        className={`min-h-11 px-6 rounded-md font-medium transition ${
                          valid && !busy
                            ? "bg-inverse text-inverse-content hover:opacity-90"
                            : "bg-surface-sunken text-content-muted cursor-not-allowed"
                        }`}
                        disabled={!valid || busy}
                      >
                        {busy ? "Loading…" : "Continue"}
                      </button>
                    </div>
                    <div id="fplIdHelp" className="mt-3 text-sm">
                      {touched && !valid ? (
                        <div className="flex items-start gap-2 text-red-700 dark:text-red-300">
                          <AlertCircle className="h-4 w-4 mt-0.5" />
                          <span>
                            Enter a valid numeric team ID (no spaces or
                            symbols).
                          </span>
                        </div>
                      ) : valid ? (
                        <div className="flex items-start gap-2 text-emerald-700 dark:text-emerald-300">
                          <CheckCircle2 className="h-4 w-4 mt-0.5" />
                          <span>Looks good! Hit Continue.</span>
                        </div>
                      ) : (
                        <div className="flex items-start gap-2 text-content-secondary">
                          <Info className="h-4 w-4 mt-0.5" />
                          <span>
                            You can find your team ID in the URL on your FPL
                            team page (e.g.{" "}
                            <code className="px-1 rounded bg-surface-sunken">
                              /entry/<strong>1234567</strong>/event
                            </code>
                            ).
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="pt-2 text-xs text-content-secondary">
                    <p>
                      Tip: Log In to{" "}
                      <a
                        className="underline"
                        target="_blank"
                        rel="noopener noreferrer"
                        href="https://account.premierleague.com/as/authorize?client_id=bfcbaf69-aade-4c1b-8f00-c1cb8a193030&redirect_uri=https%3A%2F%2Ffantasy.premierleague.com%2F&response_type=code&scope=openid+profile+email+offline_access&state=529fa7162ba34b4b885b23b165e540d9&code_challenge=Yx3e9_tWesIJ9tjZu57jPoxrws2zUY6LI54nS3gZmuk&code_challenge_method=S256"
                      >
                        Fantasy Premier League
                      </a>
                      , open <em>Points</em> and check the address bar – the
                      number after{" "}
                      <code className="px-1 rounded bg-surface-sunken">/entry/</code>{" "}
                      is your team ID.
                    </p>
                  </div>
                </form>
              </div>
            </div>
          ) : (
            // If submitted, render ManagerLeagues
            <ManagerLeagues fplId={submittedId} />
          )}
        </motion.div>
      </div>
    </div>
  );
}
